import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen, Download, Code, Question,
  Users, Rocket, ShieldCheck, Palette
} from '@phosphor-icons/react';

const MANUALS = [
  {
    id: 'operations',
    title: 'Operations Manual',
    subtitle: 'For new users, current users & admin teams',
    desc: 'Complete platform guide covering all features, user roles, admin operations, security, compliance, financial operations, and global settings.',
    icon: BookOpen,
    color: 'from-blue-500 to-cyan-400',
    file: '/manuals/01_Operations_Manual.pdf',
    audience: ['Admin', 'Affiliate', 'Advertiser'],
    pages: '~25 pages',
  },
  {
    id: 'development',
    title: 'Development Manual',
    subtitle: 'For development teams',
    desc: 'Full technical reference: architecture, API endpoints (145+), database schema (30+ collections), auth flow, WebSocket architecture, deployment, environment variables, and contributing guidelines.',
    icon: Code,
    color: 'from-purple-500 to-pink-400',
    file: '/manuals/02_Development_Manual.pdf',
    audience: ['Developers'],
    pages: '~30 pages',
  },
  {
    id: 'advertiser-affiliate',
    title: 'Advertiser & Affiliate Manual',
    subtitle: 'For advertisers and affiliates',
    desc: 'Role-specific operational guide covering affiliate earnings, smart links, storefronts, video content, live streaming, Amazon Associates, offer creation, sponsored placements, and conversion tracking.',
    icon: Users,
    color: 'from-emerald-500 to-teal-400',
    file: '/manuals/03_Advertiser_Affiliate_Manual.pdf',
    audience: ['Affiliate', 'Advertiser'],
    pages: '~20 pages',
  },
  {
    id: 'tutorial',
    title: 'Zero to Storefront Tutorial',
    subtitle: 'Step-by-step from sign-up to revenue',
    desc: '12-step walkthrough: account creation, profile setup, finding offers, building collections, uploading videos, YouTube embeds, live streaming, Amazon Associates, and performance tracking.',
    icon: Rocket,
    color: 'from-amber-500 to-orange-400',
    file: '/manuals/04_Zero_to_Storefront_Tutorial.pdf',
    audience: ['New Users'],
    pages: '~15 pages',
  },
  {
    id: 'faq',
    title: 'FAQ & Troubleshooting Guide',
    subtitle: 'Questions, answers & problem resolution',
    desc: '100+ Q&As covering accounts, tracking, video, streaming, Amazon, payments, admin operations, technical troubleshooting, API integration, and common error codes.',
    icon: Question,
    color: 'from-red-500 to-rose-400',
    file: '/manuals/05_FAQ_Troubleshooting.pdf',
    audience: ['Everyone'],
    pages: '~20 pages',
  },
  {
    id: 'brandkit',
    title: 'Brand Kit',
    subtitle: 'Logos, colours, typography & guidelines',
    desc: '15 assets: PNG logos (5 sizes, 3 variants), full wordmark (dark/light/transparent), colour palette swatch, brand guidelines PDF with typography, usage rules, and design tokens.',
    icon: Palette,
    color: 'from-pink-500 to-fuchsia-400',
    file: '/brandkit/DW_Brand_Kit_Guide.pdf',
    audience: ['Design', 'Marketing'],
    pages: '15 assets + guide',
  },
];

export default function DocumentationHub() {

  const openManual = (manual) => {
    window.open(manual.file, '_blank');
  };

  const downloadManual = (manual) => {
    const link = document.createElement('a');
    link.href = manual.file;
    link.download = manual.file.split('/').pop();
    link.click();
  };

  const downloadAll = () => {
    const link = document.createElement('a');
    link.href = '/manuals/DynamicWorld_Manuals.zip';
    link.download = 'DynamicWorld_Manuals.zip';
    link.click();
  };

  return (
    <div className="space-y-6" data-testid="documentation-hub-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Documentation Hub</h1>
          <p className="text-sm text-zinc-500 mt-1">Enterprise-grade manuals, tutorials, and brand assets</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { var a=document.createElement('a'); a.href='/brandkit/DW_Brand_Kit.zip'; a.download='DW_Brand_Kit.zip'; a.click(); }} data-testid="download-brandkit-btn" variant="outline" className="border-pink-700/40 text-pink-400 hover:bg-pink-900/20">
            <Palette size={16} className="mr-2" /> Brand Kit (ZIP)
          </Button>
          <Button onClick={downloadAll} data-testid="download-all-btn" className="bg-blue-600 hover:bg-blue-700">
            <Download size={16} className="mr-2" /> Manuals (ZIP)
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {MANUALS.map((manual) => {
          var Icon = manual.icon;
          return (
            <Card key={manual.id} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-all duration-300 group" data-testid={`manual-${manual.id}`}>
              <div className={`h-1.5 bg-gradient-to-r ${manual.color}`} />
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${manual.color} flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform`}>
                    <Icon size={22} weight="duotone" className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-zinc-100">{manual.title}</h3>
                    <p className="text-[11px] text-zinc-500">{manual.subtitle}</p>
                  </div>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">{manual.desc}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {manual.audience.map(a => (
                    <Badge key={a} variant="outline" className="text-[10px] text-zinc-400 border-zinc-700">{a}</Badge>
                  ))}
                  <Badge variant="outline" className="text-[10px] text-red-400 border-red-700/40 ml-auto">PDF</Badge>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={() => openManual(manual)} data-testid={`read-${manual.id}`}
                    className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs h-8">
                    <BookOpen size={14} className="mr-1.5" /> {manual.id === 'brandkit' ? 'View Guide' : 'Open PDF'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => {
                    if (manual.id === 'brandkit') {
                      var a = document.createElement('a'); a.href = '/brandkit/DW_Brand_Kit.zip'; a.download = 'DW_Brand_Kit.zip'; a.click();
                    } else {
                      downloadManual(manual);
                    }
                  }} data-testid={`download-${manual.id}`}
                    className="border-zinc-700 text-zinc-300 text-xs h-8 px-3">
                    <Download size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="bg-zinc-900/30 border-zinc-800/50">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <ShieldCheck size={16} className="text-emerald-400" />
            <span>All documentation is version-controlled and updated with each platform release. Current: v3.0</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
